import { useState }     from "react";
import { useNavigate }  from "react-router-dom";
import { useProfileCheck } from "../../hooks/useProfileCheck";

// ─────────────────────────────────────────────────────────────
// Severity tones now resolve through tokens instead of hardcoded
// hex, so the red/purple/blue/emerald nudge cards stay correctly
// tinted in both themes.
// ─────────────────────────────────────────────────────────────

const TONE = {
  danger:  { color: "var(--color-danger)",  bg: "var(--color-danger-light)",  border: "var(--color-danger-border)" },
  purple:  { color: "var(--color-purple)",  bg: "var(--color-purple-light)",  border: "var(--color-purple-border)" },
  info:    { color: "var(--color-info)",    bg: "var(--color-info-light)",    border: "var(--color-info-border)" },
  success: { color: "var(--color-success)", bg: "var(--color-success-light)", border: "var(--color-success-border)" },
};

const PROMPT_CONFIG = {
  phone: {
    icon: "ri-phone-line", title: "Add your phone number",
    body: "Required for transaction alerts, OTP verification, and bill payments.",
    cta: "Add phone number", path: "/profile/personal?focus=phone", tone: "danger",
  },
  transactionPin: {
    icon: "ri-lock-password-line", title: "Set your Transaction PIN",
    body: "A 4-digit PIN is required to confirm every payment.",
    cta: "Create PIN now", path: "/create-pin", tone: "purple",
  },
  name: {
    icon: "ri-user-line", title: "Complete your name",
    body: "Your full name is needed for KYC verification and receipts.",
    cta: "Update name", path: "/profile/personal?focus=firstName", tone: "purple",
  },
  profileImage: {
    icon: "ri-camera-line", title: "Add a profile photo",
    body: "Put a face to your account.",
    cta: "Upload photo", path: "/profile/personal?focus=photo", tone: "info",
  },
  not_started: {
    icon: "ri-shield-user-line", title: "Verify your identity (KYC)",
    body: "Unlock higher limits, full access, and bonus rewards.",
    cta: "Start KYC now", path: "/kyc", tone: "success",
  },
  rejected: {
    icon: "ri-shield-cross-line", title: "KYC verification failed",
    body: "Your documents were not accepted. Please resubmit.",
    cta: "Resubmit KYC", path: "/kyc", tone: "danger",
  },
  info_requested: {
    icon: "ri-information-line", title: "KYC: More information needed",
    body: "Our team needs additional info to complete your verification.",
    cta: "Provide information", path: "/kyc?action=respond", tone: "info",
  },
};

function PromptCard({ prompt, onDismiss }) {
  const navigate = useNavigate();
  const config   = prompt.type === "kyc"
    ? PROMPT_CONFIG[prompt.kycStatus]
    : PROMPT_CONFIG[prompt.key];

  if (!config) return null;
  const t = TONE[config.tone];

  return (
    <div className="flex items-start gap-3 px-4 py-3.5 rounded-2xl border relative"
      style={{ background: t.bg, borderColor: t.border }}>
      <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5"
        style={{ background: t.border }}>
        <i className={`${config.icon} text-base`} style={{ color: t.color }} />
      </div>

      <div className="flex-1 min-w-0 pr-4">
        <p className="text-sm font-bold font-display" style={{ color: t.color }}>
          {config.title}
        </p>
        <p className="text-secondary text-xs leading-relaxed mt-0.5">{config.body}</p>
        <button
          onClick={() => navigate(config.path)}
          className="mt-2.5 inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-xl transition-all active:scale-95"
          style={{ background: t.color, color: "#fff" }}>
          {config.cta}
          <i className="ri-arrow-right-line text-xs" />
        </button>
      </div>

      {prompt.dismissible ? (
        <button onClick={() => onDismiss(prompt.key)}
          className="absolute top-2.5 right-2.5 w-6 h-6 rounded-lg flex items-center justify-center hover:bg-black/5 transition-colors">
          <i className="ri-close-line text-muted text-xs" />
        </button>
      ) : (
        <div className="absolute top-2.5 right-2.5 w-6 h-6 rounded-lg flex items-center justify-center"
          style={{ background: t.border }}>
          <i className="ri-lock-line text-xs" style={{ color: t.color }} />
        </div>
      )}
    </div>
  );
}

function SummaryBadge({ prompts, hasError, onExpand }) {
  const nonDismissible = prompts.filter(p => !p.dismissible);
  const t = hasError ? TONE.danger : TONE.purple;
  const icon = hasError ? "ri-error-warning-line" : "ri-alert-line";

  const label = nonDismissible.length > 0
    ? `${nonDismissible.length} action${nonDismissible.length > 1 ? "s" : ""} required — tap to fix`
    : `${prompts.length} suggestion${prompts.length > 1 ? "s" : ""} for your profile`;

  return (
    <button onClick={onExpand}
      className="w-full flex items-center gap-3 px-4 py-3 rounded-2xl border text-left"
      style={{ background: t.bg, borderColor: t.border }}>
      <i className={`${icon} text-base flex-shrink-0`} style={{ color: t.color }} />
      <p className="flex-1 text-sm font-semibold" style={{ color: t.color }}>{label}</p>
      <i className="ri-arrow-down-s-line text-muted text-sm" />
    </button>
  );
}

function PromptSkeleton() {
  return <div className="h-[88px] bg-skeleton animate-pulse rounded-2xl" />;
}

export default function ProfilePromptBanner({ user, loading = false }) {
  const { prompts, hasWarning, hasError } = useProfileCheck(user);
  const [dismissed, setDismissed] = useState([]);
  const [collapsed, setCollapsed] = useState(true);

  if (loading) return <PromptSkeleton />;
  if (!user)   return null;

  const visible = prompts.filter(p => !p.dismissed && (!p.dismissible || !dismissed.includes(p.key)));
  if (visible.length === 0) return null;

  const handleDismiss = (key) => setDismissed(prev => [...prev, key]);
  const showCollapsed  = collapsed && visible.length > 1;

  return (
    <div className="space-y-2.5">
      {showCollapsed ? (
        <SummaryBadge prompts={visible} hasError={hasError} onExpand={() => setCollapsed(false)} />
      ) : (
        <>
          {visible.map(prompt => (
            <PromptCard key={prompt.key} prompt={prompt} onDismiss={handleDismiss} />
          ))}
          {visible.length > 1 && (
            <button onClick={() => setCollapsed(true)}
              className="w-full text-center text-xs text-muted font-medium py-1">
              Collapse
            </button>
          )}
        </>
      )}
    </div>
  );
}

// ── PhoneRequiredGate ──────────────────────────────────────
export function PhoneRequiredGate({ user, userLoading, userReady = true, children }) {
  const navigate = useNavigate();
  if (userLoading) return null;
  if (!userReady) {
    return (
      <div className="flex-1 flex flex-col px-5 pt-6 gap-4">
        {[...Array(4)].map((_, i) => (
          <div key={i} className={`bg-skeleton rounded-2xl animate-pulse ${i === 0 ? "h-24" : "h-14"}`} />
        ))}
      </div>
    );
  }

  const phoneOk = !!user?.phone?.trim();
  const pinOk   = !!user?.hasTransactionPin;
  if (phoneOk && pinOk) return children;

  const missingPhone = !phoneOk;
  const t = missingPhone ? TONE.danger : TONE.purple;
  const config = missingPhone ? {
    icon: "ri-phone-line",
    title: "Phone Number Required",
    body:  "Add a phone number to start making payments.",
    reasons: [
      "Required for transaction alerts and receipts",
      "Needed for OTP on high-value transactions",
      "Protects your account from unauthorised access",
    ],
    ctaLabel: "Add Phone Number", ctaIcon: "ri-phone-line",
    ctaPath: "/profile/personal?focus=phone",
  } : {
    icon: "ri-lock-password-line",
    title: "Transaction PIN Required",
    body:  "Set a 4-digit PIN to confirm every payment.",
    reasons: [
      "Confirms your identity before every payment",
      "Prevents unauthorised transactions",
      "Required for all bill payments and withdrawals",
    ],
    ctaLabel: "Create PIN Now", ctaIcon: "ri-lock-password-line",
    ctaPath: "/create-pin",
  };

  return (
    <div className="flex-1 flex flex-col items-center justify-center px-6 py-12 text-center space-y-5">
      <div className="w-20 h-20 rounded-3xl flex items-center justify-center" style={{ background: t.bg }}>
        <i className={`${config.icon} text-4xl`} style={{ color: t.color }} />
      </div>
      <div>
        <p className="text-primary font-bold text-xl mb-2 font-display">{config.title}</p>
        <p className="text-secondary text-sm leading-relaxed max-w-[280px] mx-auto">{config.body}</p>
      </div>
      <div className="w-full bg-surface-muted border border-border-strong rounded-2xl px-4 py-4 text-left space-y-2.5">
        {config.reasons.map((text, i) => (
          <div key={i} className="flex items-start gap-2.5">
            <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
              style={{ background: t.bg }}>
              <i className="ri-check-line text-[10px]" style={{ color: t.color }} />
            </div>
            <p className="text-secondary text-xs leading-relaxed">{text}</p>
          </div>
        ))}
      </div>
      {!phoneOk && !pinOk && (
        <div className="w-full bg-warning-light border border-warning-border rounded-xl px-3 py-2.5 flex items-center gap-2">
          <i className="ri-information-line text-sm flex-shrink-0" style={{ color: "var(--color-warning)" }} />
          <p className="text-xs" style={{ color: "var(--color-warning-text)" }}>Also set a <strong>Transaction PIN</strong> before you can pay.</p>
        </div>
      )}
      <button onClick={() => navigate(config.ctaPath)}
        className="w-full py-4 text-on-brand font-bold text-sm rounded-2xl transition-all active:scale-95 flex items-center justify-center gap-2"
        style={{ background: t.color }}>
        <i className={config.ctaIcon} />{config.ctaLabel}
      </button>
      <button onClick={() => navigate(-1)} className="text-muted text-sm font-medium">Go back</button>
    </div>
  );
}